import React, { useState, useEffect } from 'react';
import './NoteDetailOverlay.css';
import { formatText } from '../../utils/typografUtils';
import { applyGender, getCachedGender } from '../../utils/genderUtils';
import { getAllUserNotesForCard } from '../../api/notes';
import { saveUserNote } from '../../api/notes';

export default function NoteDetailOverlay({ note, imageUrl, onClose, onNoteSaved }) {
    const [allNotes, setAllNotes] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [showNoteWriting, setShowNoteWriting] = useState(false);
    const [noteText, setNoteText] = useState('');
    const [isNoteLoading, setIsNoteLoading] = useState(false);
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);

    // Загружаем все заметки для этой карточки
    useEffect(() => {
        const loadAllNotes = async () => {
            if (note?.cardId) {
                setIsLoading(true);
                try {
                    const notes = await getAllUserNotesForCard(note.cardId);
                    setAllNotes(notes);
                } catch (error) {
                    console.error('Ошибка при загрузке заметок:', error);
                } finally {
                    setIsLoading(false);
                }
            } else {
                setIsLoading(false);
            }
        };

        loadAllNotes();
    }, [note?.cardId]);

    // Проверка после всех хуков
    if (!note) return null;

    // Функция для форматирования даты
    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '';

        const today = new Date();
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);

        const time = date.toLocaleTimeString('ru-RU', {
            hour: '2-digit',
            minute: '2-digit'
        });

        if (date.toDateString() === today.toDateString()) {
            return `Сегодня в ${time}`;
        } else if (date.toDateString() === yesterday.toDateString()) {
            return `Вчера в ${time}`;
        } else {
            const day = date.getDate();
            const month = date.toLocaleDateString('ru-RU', { month: 'numeric' });
            const year = date.getFullYear();
            return `${day}.${month}.${year} в ${time}`;
        }
    };

    // Используем переданный URL или fallback
    const displayImage = imageUrl || '/placeholder-card.png';

    // Открыть режим создания заметки
    const handleOpenNoteWriting = () => {
        setShowNoteWriting(true);
        setNoteText('');
    };

    // Закрыть режим создания заметки
    const handleCloseNoteWriting = () => {
        if (noteText.trim()) {
            // Если есть текст в заметке, показываем диалог подтверждения
            setShowConfirmDialog(true);
        } else {
            // Если заметка пустая, закрываем без подтверждения
            setShowNoteWriting(false);
            setNoteText('');
        }
    };

    // Функция для подтверждения удаления черновика
    const handleConfirmDelete = () => {
        setShowConfirmDialog(false);
        setShowNoteWriting(false);
        setNoteText('');
    };

    // Функция для отмены удаления черновика
    const handleCancelDelete = () => {
        setShowConfirmDialog(false);
    };

    // Сохранить заметку
    const handleSaveNote = async () => {
        if (!noteText.trim()) return;

        setIsNoteLoading(true);
        try {
            await saveUserNote(note.gameId, note.cardId, noteText.trim());
            setShowNoteWriting(false);
            setNoteText('');
            // Перезагружаем список заметок
            const notes = await getAllUserNotesForCard(note.cardId);
            setAllNotes(notes);
            // Уведомляем родительский компонент о сохранении заметки
            if (onNoteSaved) {
                onNoteSaved();
            }
        } catch (error) {
            console.error('Ошибка при сохранении заметки:', error);
            alert('Не удалось сохранить заметку. Попробуйте еще раз.');
        } finally {
            setIsNoteLoading(false);
        }
    };

    // Обработчик изменения текста заметки
    const handleNoteTextChange = (e) => {
        setNoteText(e.target.value);
    };

    return (
        <>
            <div className="overlay overlay--note-detail" role="dialog" aria-modal="true">
                {/* Размытый фон */}
                <div
                    className="overlay-bg overlay-bg--current"
                    style={{ backgroundImage: `url(${displayImage})` }}
                />

                {/* Заголовок */}
                <div className="upper-button-wrap">
                    <div className="upper-buttons">
                        <button
                            className="btn-40"
                            onClick={showNoteWriting ? handleCloseNoteWriting : onClose}
                            aria-label={showNoteWriting ? "Закрыть создание заметки" : "Закрыть"}
                        >
                            <img src="/icons/close.svg" alt="" />
                        </button>
                        <div className="progress-badge">
                            {showNoteWriting ? <span className="note-title">Новая заметка</span> : "Заметка"}
                        </div>
                        <button
                            className={`btn-40 ${!showNoteWriting || (showNoteWriting && noteText.trim()) ? 'btn-40--active' : ''}`}
                            onClick={showNoteWriting ? handleSaveNote : handleOpenNoteWriting}
                            disabled={showNoteWriting && (!noteText.trim() || isNoteLoading)}
                            aria-label={showNoteWriting ? "Сохранить заметку" : "Создать заметку"}
                        >
                            {showNoteWriting ? (
                                isNoteLoading ? (
                                    <div className="loading-spinner-small" />
                                ) : (
                                    <img src="/icons/checkmark.svg" alt="" />
                                )
                            ) : (
                                <img src="/icons/note-white.svg" alt="" />
                            )}
                        </button>
                    </div>
                </div>

                {/* Основное содержимое заметки */}
                <div className="note-writing-content">
                    {/* Карточка с вопросом */}
                    <div className="note-question-card">
                        <div className="note-question-image">
                            <img
                                src={displayImage}
                                alt="Карточка"
                                loading="eager"
                            />
                        </div>
                        <div className="note-writing-text">
                            <h3 className="note-question-title">{formatText(applyGender(note.cardQuestion, getCachedGender()))}</h3>
                            {note.cardSubquestion && (
                                <p className="note-question-subtitle">{formatText(applyGender(note.cardSubquestion, getCachedGender()))}</p>
                            )}
                        </div>
                    </div>

                    {showNoteWriting ? (
                        /* Режим создания заметки */
                        <div className="note-input-card">
                            <textarea
                                className="note-textarea"
                                placeholder="Все ваши заметки защищены шифрованием и доступны только вам"
                                value={noteText}
                                onChange={handleNoteTextChange}
                                maxLength={2000}
                                autoFocus
                            />
                        </div>
                    ) : (
                        /* Режим просмотра заметок */
                        <div className="notes-container">
                            {/* Первая заметка - текущая, показываем сразу из пропсов */}
                            <div className="note-display-card note-display-card--view">
                                <div className="note-bubble">
                                    <div className="note-bubble-text">
                                        {note.content}
                                    </div>
                                    <div className="note-bubble-date">
                                        {formatDate(note.updatedAt)}
                                    </div>
                                </div>
                            </div>

                            {/* Остальные заметки - загружаем с сервера */}
                            {!isLoading && allNotes.length > 0 && (
                                <>
                                    {allNotes
                                        .filter(noteItem => noteItem.id !== note.id) // Исключаем текущую заметку
                                        .map((noteItem) => (
                                            <div key={noteItem.id} className="note-display-card note-display-card--view">
                                                <div className="note-bubble">
                                                    <div className="note-bubble-text">
                                                        {noteItem.content}
                                                    </div>
                                                    <div className="note-bubble-date">
                                                        {formatDate(noteItem.updatedAt || noteItem.createdAt)}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                </>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Диалог подтверждения удаления черновика */}
            {
                showConfirmDialog && (
                    <div className="confirm-dialog-overlay" style={{ zIndex: 3000 }}>
                        <div className="confirm-dialog">
                            <div className="confirm-dialog-content">
                                <h3 className="confirm-dialog-title">Удалить черновик заметки?</h3>
                                <p className="confirm-dialog-message">Ваш текст будет потерян</p>
                            </div>
                            <div className="confirm-dialog-buttons">
                                <button
                                    className="confirm-dialog-btn confirm-dialog-btn--cancel"
                                    onClick={handleCancelDelete}
                                >
                                    Вернуться
                                </button>
                                <button
                                    className="confirm-dialog-btn confirm-dialog-btn--delete"
                                    onClick={handleConfirmDelete}
                                >
                                    Удалить
                                </button>
                            </div>
                        </div>
                    </div>
                )
            }
        </>
    );
}
